import { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import ResourceModal from 'app/components/ResourceModal';
import ResourceModalPanel from 'app/components/ResourceModalPanel';
import PrintSizeForm from 'app/components/PrintSizeForm';
import KioskLocaleForm from 'app/components/KioskLocaleForm';
import KioskPricingForm from 'app/components/KioskPricingForm';
import KioskForm from './KioskForm';

import {
  populatePrintSizes,
  getKioskPrintSizes,
  createKioskPricing,
  updateKioskPricing,
  deleteKioskPricing
} from 'app/actions/kiosks';

import {
  getKioskLocales,
  toggleEnablePrintSize,
} from 'app/services/kiosks';

import {
  getKioskPricings
} from 'shared/services/PriceService';

const KioskPanel = props => {
  const {
    kiosk,
    action,
    enabledLocales,
    setShowKioskLocales,
    selectedPrintSizes,
    setShowKioskPrintSizes,
    kioskPricing,
    setShowKioskPricing
  } = props;

  // if we're creating a kiosk then no actions panel will be shown
  if (action === 'create') {
    return null;
  }

  let cards = [
    {
      label: 'Manage Locales',
      details: [
        { label: 'Locales', value: enabledLocales.map((x) => x.locale).join(', ') },
      ],
      actions: [
        { label: 'Update Locales', callback: () => setShowKioskLocales(true) }
      ]
    },
    {
      label: 'Manage Print Sizes',
      details: [
        { label: 'Print Sizes', value: selectedPrintSizes.map((x) => x.size).join(', ') },
      ],
      actions: [
        { label: 'Update PrintSize', callback: () => setShowKioskPrintSizes(true) }
      ]
    },
    {
      label: 'Kiosk Pricing',
      details: [
        { label: 'Pricing', value: kioskPricing.map((x) => x.store_price).join(', ') },
      ],
      actions: [
        { label: 'Update Kiosk Pricing', callback: () => setShowKioskPricing(true) }
      ]
    }
  ]

  return <ResourceModalPanel cards={cards} />;
}

const KioskModal = props => {
  const {
    isOpen,
    onClose,
    onFormSubmit,
    kiosk,
    account,
    populatePrintSizes,
    getKioskPrintSizes,
    updateKioskPricing,
    createKioskPricing,
    deleteKioskPricing,
    jobConfigs,
    jobs,
  } = props;

  const [showKioskLocales, setShowKioskLocales] = useState(false);
  const [showKioskPricing, setShowKioskPricing] = useState(false);
  const [showKioskPrintSizes, setShowKioskPrintSizes] = useState(false);
  const [kioskLocales, setKioskLocales] = useState([]);
  const [enabledLocales, setEnabledLocales] = useState([]);
  const [printSizes, setPrintSizes] = useState([]);
  const [selectedPrintSizes, setSelectedPrintSizes] = useState([]);
  const [kioskPricing, setKioskPricing] = useState([]);

  // load resources on first render
  useEffect(() => {
    if (kiosk) {
      populatePrintSizes(kiosk?.id)
        .then(printSizes => setPrintSizes(printSizes));

      getKioskPrintSizes(kiosk?.id)
        .then(selectedPrintSizes => setSelectedPrintSizes(selectedPrintSizes));

    }
  }, [kiosk]);

  // load kiosk info on change
  useEffect(() => {
    if (kiosk) {
      getKioskPricings(kiosk.id)
        .then(pricings => setKioskPricing(pricings));

      getKioskLocales(kiosk.id).then((locales) => {
        setEnabledLocales(locales.filter(x => x.enabled));
        setKioskLocales(locales);
      });
    }
  }, [kiosk]);

  const action = kiosk ? 'update' : 'create';

  let title, subTitle = null;
  if (action === 'update') {
    title = kiosk.name;
    subTitle = kiosk.header;
  } else {
    title = 'New Kiosk';
  }

  const onSubmitKioskLocales = localeMap => {
    const locales = Object.values(localeMap);

    setEnabledLocales(locales.filter(x => x.enabled));
    setKioskLocales(locales);
    setShowKioskLocales(false);
  }

  const onSubmitPrintSizes = async selectedPrintSizes => {
    const sizeKeys = Object.keys(selectedPrintSizes);
    for (let key of sizeKeys) {
      const printSize = printSizes.find(x => x.size == key);

      if (printSize) {
        let printSizeEnabled = selectedPrintSizes[key];
        await toggleEnablePrintSize(kiosk.id, printSize.id, printSizeEnabled);
      }
    }

    getKioskPrintSizes(kiosk.id)
      .then(setSelectedPrintSizes);

    setShowKioskPrintSizes(false);
  }

  const onSubmitKioskPricing = async (pricings, origPricings) => {
    for (let item of pricings) {

      // Need to convert numbers to strings to store currency values properly
      if (typeof item.store_price === 'number') {
        item.store_price = item.store_price.toFixed(2)
      }

      if (item.id) {
        await updateKioskPricing(kiosk.id, item.id, item);
      } else {
        await createKioskPricing(kiosk.id, item);
      }
    }

    let pricingIds = pricings.reduce((list, pricing) => {
      list.push(pricing.id)
      return list
    }, [])

    let forDeletes = origPricings.filter(pricing => {
      return pricingIds.indexOf(pricing.id) < 0;
    });

    for (let item of forDeletes) {
      await deleteKioskPricing(kiosk.id, item.id);
    }


    setKioskPricing(pricings);
    setShowKioskPricing(false);
  }

  let kioskForms = null;
  if (kiosk) {
    kioskForms = (
      <div>
        {showKioskLocales && <KioskLocaleForm
          isOpen={showKioskLocales}
          kiosk={kiosk}
          kioskLocales={kioskLocales}
          onSubmit={onSubmitKioskLocales}
          onClose={() => setShowKioskLocales(false)}
        />}
        {showKioskPrintSizes && <PrintSizeForm
          isOpen={showKioskPrintSizes}
          printSizes={printSizes}
          selectedPrintSizes={selectedPrintSizes}
          onSubmit={onSubmitPrintSizes}
          onClose={() => setShowKioskPrintSizes(false)}
        />}
        {showKioskPricing && <KioskPricingForm
          isOpen={showKioskPricing}
          kiosk={kiosk}
          onClose={() => setShowKioskPricing(false)}
          onSubmit={onSubmitKioskPricing}
        />}
      </div>
    )
  }

  const onSubmit = params => onFormSubmit(action, params);

  return (
    <ResourceModal {...{ isOpen, onClose, title, subTitle }}>
      <KioskPanel {...{
        kiosk,
        action,
        onClose,
        enabledLocales,
        setShowKioskLocales,
        selectedPrintSizes,
        setShowKioskPrintSizes,
        kioskPricing,
        setShowKioskPricing
      }} />
      <KioskForm {...{
        kiosk,
        action,
        onClose,
        onSubmit,
        jobConfigs,
        jobs,
      }} />
      {kioskForms}
    </ResourceModal>
  );
}

const mapStateToProps = state => ({
  account: state.portalReducer.account
});

export default connect(mapStateToProps, { populatePrintSizes, getKioskPrintSizes, createKioskPricing, updateKioskPricing, deleteKioskPricing })(KioskModal);
